import { useEffect, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import {
  Bold, Italic, Strikethrough, Code, Heading2, Heading3, List, ListOrdered,
  Quote, Minus, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Pilcrow,
} from "lucide-react";
import { toast } from "sonner";
import { uploadToCloudinary } from "@/lib/uploads";

/**
 * Headless TipTap-based WYSIWYG editor. Outputs sanitized-friendly HTML
 * matching the backend's bleach allow-list (p, h2-3, strong/em, lists,
 * blockquote, code, links, images, hr).
 *
 * Props:
 *   value       — current HTML string
 *   onChange    — (html) => void, called on every change
 *   placeholder — shown when empty (visual only; via CSS)
 *   testidPrefix — for test selectors (default 'rte')
 */
export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Write…",
  testidPrefix = "rte",
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        link: false, // disabled — we add the standalone Link extension below for our custom config
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({
        HTMLAttributes: { class: "rounded-lg border border-stone-200" },
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class:
          "prose prose-stone max-w-none min-h-[260px] p-4 focus:outline-none rounded-b-xl bg-white",
        "data-placeholder": placeholder,
        "data-testid": `${testidPrefix}-content`,
      },
    },
    onUpdate: ({ editor: e }) => onChange?.(e.getHTML()),
  });

  // Sync external value changes (e.g. when switching between articles)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL (leave empty to remove):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url, target: "_blank" })
      .run();
  }, [editor]);

  const addImage = useCallback(async (file) => {
    if (!editor || !file) return;
    try {
      const res = await uploadToCloudinary(file);
      const src = res?.secure_url;
      if (!src) throw new Error("No URL returned");
      editor.chain().focus().setImage({ src, alt: file.name || "" }).run();
    } catch (e) {
      toast.error("Image upload failed");
      console.error(e);
    }
  }, [editor]);

  // Handle paste/drop of images at the editor level
  useEffect(() => {
    if (!editor) return;
    const el = editor.view.dom;
    const onPaste = async (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imgItem = items.find((it) => it.type.startsWith("image/"));
      if (imgItem) {
        e.preventDefault();
        const file = imgItem.getAsFile();
        if (file) await addImage(file);
      }
    };
    const onDrop = async (e) => {
      const files = Array.from(e.dataTransfer?.files || []);
      const imgFile = files.find((f) => f.type.startsWith("image/"));
      if (imgFile) {
        e.preventDefault();
        await addImage(imgFile);
      }
    };
    el.addEventListener("paste", onPaste);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("paste", onPaste);
      el.removeEventListener("drop", onDrop);
    };
  }, [editor, addImage]);

  if (!editor) return <div className="border border-stone-200 rounded-xl min-h-[260px]" />;

  const ToolbarBtn = ({ onClick, active, disabled, title, testid, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={`${testidPrefix}-${testid}`}
      className={`h-8 w-8 grid place-items-center rounded transition-colors ${
        active
          ? "bg-yellow-400/30 text-amber-900"
          : "text-stone-600 hover:bg-stone-100"
      } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );

  return (
    <div
      className="rounded-xl border border-stone-200 overflow-hidden bg-white"
      data-testid={`${testidPrefix}-wrapper`}
    >
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-stone-200 bg-stone-50 sticky top-0 z-10">
        <ToolbarBtn
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive("paragraph")}
          title="Paragraph"
          testid="paragraph"
        >
          <Pilcrow className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
          testid="h2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          title="Heading 3"
          testid="h3"
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-stone-300 mx-1" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
          testid="bold"
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
          testid="italic"
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
          title="Strikethrough"
          testid="strike"
        >
          <Strikethrough className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive("code")}
          title="Inline code"
          testid="code"
        >
          <Code className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-stone-300 mx-1" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bulleted list"
          testid="ul"
        >
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Numbered list"
          testid="ol"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          title="Quote"
          testid="quote"
        >
          <Quote className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Divider"
          testid="hr"
        >
          <Minus className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-stone-300 mx-1" />
        <ToolbarBtn
          onClick={addLink}
          active={editor.isActive("link")}
          title="Link"
          testid="link"
        >
          <LinkIcon className="w-4 h-4" />
        </ToolbarBtn>
        <label
          className="h-8 w-8 grid place-items-center rounded text-stone-600 hover:bg-stone-100 cursor-pointer"
          title="Insert image"
          data-testid={`${testidPrefix}-image-label`}
        >
          <ImageIcon className="w-4 h-4" />
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) await addImage(f);
              e.target.value = "";
            }}
          />
        </label>
        <span className="flex-1" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
          testid="undo"
        >
          <Undo className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
          testid="redo"
        >
          <Redo className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
